/**
 * Pick which customer/specialty mapping the dictionary preview should use.
 * Prefer revops_orgs when the doctor has rows there. If they do not, fall
 * back to the single doctors.super_org + doctors.specialty pair (then
 * doctors.organization if super_org is empty/placeholder). Never mix the
 * leftover doctors columns into a doctor who already has revops mappings.
 * A URL org+specialty pair that is not in the mapping list is not invented.
 */
const PLACEHOLDER_VALUES = new Set([
    "n/a",
    "na",
    "unknown",
    "none",
    "-",
    "—",
]);

export const isUsableScopeValue = (value) => {
    const text = String(value ?? "").trim();
    if (!text) return false;
    return !PLACEHOLDER_VALUES.has(text.toLowerCase());
};

const firstUsable = (...values) => {
    for (const value of values) {
        if (isUsableScopeValue(value)) return String(value).trim();
    }
    return null;
};

const normalizeMapping = (row) => ({
    org: firstUsable(row?.org) || "",
    specialty: firstUsable(row?.specialty) || "",
});

export const mappingFromDoctorRow = (doctor) => {
    const org = firstUsable(doctor?.super_org, doctor?.organization);
    if (!org) return null;
    return {
        org,
        specialty: firstUsable(doctor?.specialty) || "",
    };
};

export const pickDoctorMapping = (mappings = [], { org, specialty } = {}) => {
    const rows = (mappings || [])
        .map(normalizeMapping)
        .filter((row) => row.org);
    if (rows.length === 0) {
        return { mapping: null, ambiguous: false, candidates: [] };
    }

    const wantedOrg = firstUsable(org) || "";
    const wantedSpecialty = firstUsable(specialty)
        ? String(specialty).trim().toLowerCase()
        : "";

    const pool = wantedOrg
        ? rows.filter((row) => String(row.org) === wantedOrg)
        : rows;

    if (wantedOrg && pool.length === 0) {
        if (rows.length === 1) {
            return { mapping: rows[0], ambiguous: false, candidates: rows };
        }
        return { mapping: null, ambiguous: true, candidates: rows };
    }

    if (wantedSpecialty) {
        const specPool = pool.filter(
            (row) =>
                String(row.specialty || "").toLowerCase() === wantedSpecialty,
        );
        if (specPool.length > 0) {
            return { mapping: specPool[0], ambiguous: false, candidates: pool };
        }
    }

    if (pool.length === 1) {
        return { mapping: pool[0], ambiguous: false, candidates: pool };
    }

    return { mapping: null, ambiguous: true, candidates: pool };
};

export const resolveDoctorPreviewContext = ({
    mappings = [],
    org,
    specialty,
    doctor,
} = {}) => {
    const rows = (mappings || []).map(normalizeMapping).filter((row) => row.org);

    if (rows.length === 0) {
        const fallback = mappingFromDoctorRow(doctor);
        return {
            org: fallback?.org || null,
            specialty: firstUsable(fallback?.specialty),
            ambiguous: false,
            candidates: [],
        };
    }

    const picked = pickDoctorMapping(rows, { org, specialty });
    return {
        org: firstUsable(picked.mapping?.org),
        specialty: firstUsable(picked.mapping?.specialty),
        ambiguous: picked.ambiguous,
        candidates: picked.candidates,
    };
};
